import React, { useRef, useState } from "react";
import { Box, Dialog, Divider, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import SuccessDialogCst from "@components/SuccessDialogCst";
import { openToast } from "@utils/toastStore";
import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";

export type RefreshPerimeterTarget =
  { scope: "group"; group: any } | { scope: "all" };

interface RefreshPerimeterDialogProps {
  target: RefreshPerimeterTarget | null;
  corporateId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const RefreshPerimeterDialog: React.FC<RefreshPerimeterDialogProps> = ({
  target,
  corporateId,
  onClose,
  onSuccess,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("users-groups-v2");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const lastTarget = useRef<RefreshPerimeterTarget | null>(null);

  if (target) lastTarget.current = target;
  const current = target ?? lastTarget.current;
  const isGroup = current?.scope === "group";

  const close = () => {
    onClose();
    setIsDone(false);
  };

  const confirm = async () => {
    if (!target) return;
    const url =
      target.scope === "group"
        ? ServicesURL.refreshDynamicGroup
            .replace(":idCorporate", corporateId)
            .replace(":idGroup", String(target.group?.id))
        : ServicesURL.refreshDynamicGroups.replace(":idCorporate", corporateId);
    setIsSubmitting(true);
    try {
      await fetcher(url, MethodType.PUT, {});
      setIsDone(true);
      onSuccess && onSuccess();
    } catch (error) {
      console.error(error);
      openToast({
        severity: "error",
        message: String(t("confirm-dialog.error")),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const titleSx = {
    fontSize: "24px",
    fontWeight: 700,
    lineHeight: 1.2,
    color: theme.palette.text.primary,
    overflowWrap: "anywhere",
  };
  const textSx = {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  };

  return (
    <>
      <Dialog
        open={!!target && !isDone}
        onClose={isSubmitting ? undefined : onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: "600px",
            backgroundColor: theme.palette.background.default,
            borderRadius: "16px",
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: "16px",
            padding: "32px",
            color: theme.palette.text.primary,
          }}
        >
          <RotateCcw size={40} />
          <Typography sx={titleSx}>
            {isGroup
              ? t("refresh-perimeter-dialog.title-group", {
                  name:
                    current?.scope === "group"
                      ? String(current.group?.name ?? "").trim()
                      : "",
                  interpolation: { escapeValue: false },
                })
              : t("refresh-perimeter-dialog.title-all")}
          </Typography>
          <Typography sx={textSx}>
            {t(
              isGroup
                ? "refresh-perimeter-dialog.description-group"
                : "refresh-perimeter-dialog.description-all",
            )}
          </Typography>
          <Divider flexItem />
          <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
            <ButtonSecondary
              size="md"
              onClick={onClose}
              disabled={isSubmitting}
              sx={{ flex: 1 }}
            >
              {t("confirm-dialog.cancel")}
            </ButtonSecondary>
            <ButtonPrimary
              size="md"
              onClick={confirm}
              disabled={isSubmitting}
              sx={{ flex: 1 }}
            >
              {t("confirm-dialog.confirm")}
            </ButtonPrimary>
          </Box>
        </Box>
      </Dialog>
      <SuccessDialogCst
        open={!!target && isDone}
        title={String(t("refresh-perimeter-dialog.success-title"))}
        description={String(
          t(
            isGroup
              ? "refresh-perimeter-dialog.success-group"
              : "refresh-perimeter-dialog.success-all",
          ),
        )}
        onClose={close}
        showClose
      />
    </>
  );
};

export default RefreshPerimeterDialog;
